import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { RefreshCw, KeyRound, Trash2, Smartphone, Globe2, CreditCard, Receipt, Bell, Save } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { useSession } from '../../session/SessionProvider';
import { errorText } from '../../lib/errors';
import { Drawer, Tabs, Avatar, Badge, Button, Field, Input, Select, Modal, ConfirmDialog, LoadingBlock, EmptyState } from '../../ui';
import { employeeName } from './EmployeesPage';

const TRAN_TOPUP = 1;

export default function EmployeeDrawer({ userId, onClose, onChanged }) {
  const { t } = useI18n();
  const { canEdit, isOwner, isViewingAsOwner } = useSession();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('profile');
  const [syncing, setSyncing] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Родитель передаёт новую функцию при каждой перерисовке — не перезагружаем карточку из-за этого
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.get(`/admin/user/${userId}`);
      setData(res.data);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
      onCloseRef.current();
    }
  }, [userId, t]);

  useEffect(() => { setData(null); setTab('profile'); load(); }, [load]);

  if (!userId) return null;
  const user = data?.user;
  const name = user ? employeeName(user) : '';

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post(`/admin/update-e-wallet/${userId}`);
      toast.success(t('employees.synced'));
      await load();
      onChanged();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSyncing(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/user/${userId}`);
      toast.success(t('employees.deleted'));
      setDeleteOpen(false);
      onClose();
      onChanged();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={!!userId}
      onClose={onClose}
      title={user ? (name || user.phone) : t('common.loading')}
      subtitle={user && (
        <span className="tabular">
          {[name ? user.phone : null, isOwner && !isViewingAsOwner ? user.tenant_name : null].filter(Boolean).join(' · ')}
        </span>
      )}
      footer={user && canEdit && (
        <>
          <Button variant="danger-ghost" icon={Trash2} onClick={() => setDeleteOpen(true)} className="mr-auto">{t('employees.deleteEmployee')}</Button>
          <Button variant="secondary" icon={KeyRound} onClick={() => setPasswordOpen(true)}>{t('employees.resetPassword')}</Button>
          <Button variant="secondary" icon={RefreshCw} onClick={sync} loading={syncing}>{t('employees.syncPos')}</Button>
        </>
      )}
    >
      {!data ? <LoadingBlock /> : (
        <div className="px-6 py-5">
          <div className="mb-5 flex items-center gap-4">
            <Avatar name={name || user.phone} src={user.avatar_url || undefined} size="lg" />
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap gap-1.5">
                {user.email ? <Badge tone="success" dot>{t('employees.registeredYes')}</Badge> : <Badge dot>{t('employees.registeredNo')}</Badge>}
                {user.telegram_chat_id && <Badge tone="brand">Telegram</Badge>}
              </div>
              <p className="truncate text-sm text-zinc-500">{user.email || '—'}</p>
            </div>
          </div>

          <Tabs
            className="mb-5"
            value={tab}
            onChange={setTab}
            items={[
              { id: 'profile', label: t('employees.profileTab') },
              { id: 'cards', label: t('employees.cardsTab'), icon: CreditCard, count: data.cards.length },
              { id: 'history', label: t('employees.historyTab'), icon: Receipt },
              { id: 'notifications', label: t('employees.notificationsTab'), icon: Bell },
            ]}
          />

          {tab === 'profile' && <ProfileSection user={user} canEdit={canEdit} onSaved={() => { load(); onChanged(); }} />}
          {tab === 'cards' && <CardsSection cards={data.cards} />}
          {tab === 'history' && <HistorySection userId={userId} />}
          {tab === 'notifications' && <NotificationsSection userId={userId} />}

          <PasswordModal open={passwordOpen} onClose={() => setPasswordOpen(false)} userId={userId} />
          <ConfirmDialog
            open={deleteOpen}
            onClose={() => setDeleteOpen(false)}
            onConfirm={remove}
            loading={busy}
            title={t('employees.deleteTitle')}
            text={t('employees.deleteText')}
            confirmLabel={t('common.delete')}
          />
        </div>
      )}
    </Drawer>
  );
}

function InfoBlock({ icon: Icon, label, children }) {
  return (
    <div className="flex gap-3 rounded-lg border border-zinc-200 px-3 py-2.5 dark:border-zinc-800">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" />
      <div className="min-w-0 text-sm">
        <p className="text-xs text-zinc-500 dark:text-zinc-400">{label}</p>
        <div className="text-zinc-900 dark:text-zinc-100">{children}</div>
      </div>
    </div>
  );
}

function ProfileSection({ user, canEdit, onSaved }) {
  const { t, fmt } = useI18n();
  const initial = {
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    middle_name: user.middle_name || '',
    birth_date: user.birth_date ? String(user.birth_date).slice(0, 10) : '',
    sex: String(user.sex ?? 0),
  };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/user/${user.user_id}/profile`, { ...form, sex: Number(form.sex) });
      toast.success(t('employees.profileSaved'));
      onSaved();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const where = (ip, country) => [country, ip].filter(Boolean).join(' · ');

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <InfoBlock icon={Smartphone} label={t('employees.device')}>
          {[user.device_model, user.os_version && `iOS ${user.os_version}`].filter(Boolean).join(' · ') || '—'}
          {user.app_version && <AppVersionLine raw={user.app_version} />}
        </InfoBlock>
        <InfoBlock icon={Globe2} label={t('employees.lastLoginFrom')}>
          {user.last_login_at ? fmt.dateTime(user.last_login_at) : t('common.never')}
          {where(user.last_login_ip, user.last_login_country) && <p className="text-xs text-zinc-500">{where(user.last_login_ip, user.last_login_country)}</p>}
        </InfoBlock>
        <InfoBlock icon={Globe2} label={t('employees.registeredFrom')}>
          {fmt.dateTime(user.created_at)}
          {where(user.registration_ip, user.registration_country) && <p className="text-xs text-zinc-500">{where(user.registration_ip, user.registration_country)}</p>}
        </InfoBlock>
      </div>

      <form onSubmit={save} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label={t('employees.lastName')}><Input value={form.last_name} onChange={set('last_name')} disabled={!canEdit} maxLength={100} /></Field>
          <Field label={t('employees.firstName')}><Input value={form.first_name} onChange={set('first_name')} disabled={!canEdit} maxLength={100} /></Field>
          <Field label={t('employees.middleName')}><Input value={form.middle_name} onChange={set('middle_name')} disabled={!canEdit} maxLength={100} /></Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('employees.birthDate')}><Input type="date" value={form.birth_date} onChange={set('birth_date')} disabled={!canEdit} /></Field>
          <Field label={t('employees.sex')}>
            <Select value={form.sex} onChange={set('sex')} disabled={!canEdit}>
              {['0', '1', '2'].map((s) => <option key={s} value={s}>{t(`employees.sexes.${s}`)}</option>)}
            </Select>
          </Field>
        </div>
        {canEdit && (
          <div className="flex justify-end">
            <Button type="submit" icon={Save} loading={saving} disabled={!dirty}>{t('common.save')}</Button>
          </div>
        )}
      </form>
    </div>
  );
}

function CardsSection({ cards }) {
  const { t, fmt } = useI18n();
  if (!cards.length) return <EmptyState icon={CreditCard} title={t('employees.noCards')} />;
  return (
    <ul className="space-y-3">
      {cards.map((c) => (
        <li key={`${c.tenant_id}-${c.card_id}`} className="rounded-xl border border-zinc-200 p-4 dark:border-zinc-800">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-medium text-zinc-900 dark:text-zinc-100">{c.loyalty_program || '—'}</p>
              <p className="font-mono text-xs text-zinc-500">{c.short_code || c.ewallet_code || c.card_id}</p>
            </div>
            {c.is_active === 0 && <Badge tone="neutral">{t('employees.inactiveCard')}</Badge>}
          </div>
          <div className="mt-3 flex items-end justify-between gap-3">
            <div>
              <p className="text-xs text-zinc-500">{t('employees.balance')}</p>
              <p className="tabular text-xl font-semibold text-zinc-900 dark:text-zinc-50">{fmt.money(c.pay_sum)}</p>
            </div>
            {c.validity_end && (
              <p className="text-right text-xs text-zinc-500">{t('employees.validUntil')}<br /><span className="tabular text-zinc-700 dark:text-zinc-300">{fmt.date(c.validity_end)}</span></p>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function HistorySection({ userId }) {
  const { t, fmt } = useI18n();
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/admin/user/${userId}/transactions`, { params: { limit: 100 } })
      .then((res) => setItems(res.data.transactions || []))
      .catch(() => setItems([]));
  }, [userId]);
  if (!items) return <LoadingBlock />;
  if (!items.length) return <EmptyState icon={Receipt} title={t('employees.noHistory')} />;
  return (
    <ul className="divide-y divide-zinc-100 rounded-xl border border-zinc-200 dark:divide-zinc-800 dark:border-zinc-800">
      {items.map((tr) => {
        const topup = tr.tran_type === TRAN_TOPUP;
        return (
          <li key={tr.id} className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">{tr.restaurant_name || '—'}</p>
              <p className="tabular text-xs text-zinc-500">{fmt.posDateTime(tr.tran_date)} · {topup ? t('employees.topup') : t('employees.payment')}</p>
            </div>
            <p className={`tabular shrink-0 text-sm font-medium ${topup ? 'text-emerald-600 dark:text-emerald-400' : 'text-zinc-900 dark:text-zinc-100'}`}>
              {topup ? '+' : '−'}{fmt.money(Math.abs(tr.tran_sum))}
            </p>
          </li>
        );
      })}
    </ul>
  );
}

function NotificationsSection({ userId }) {
  const { t, fmt } = useI18n();
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/admin/user/${userId}/notifications`)
      .then((res) => setItems(res.data.notifications || []))
      .catch(() => setItems([]));
  }, [userId]);
  if (!items) return <LoadingBlock />;
  if (!items.length) return <EmptyState icon={Bell} title={t('employees.noNotifications')} />;
  return (
    <ul className="space-y-2">
      {items.map((n) => (
        <li key={n.id} className="rounded-lg border border-zinc-200 px-4 py-3 dark:border-zinc-800">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{n.title}</p>
            <Badge tone={n.read_at ? 'neutral' : 'brand'}>{n.read_at ? t('employees.read') : t('employees.unread')}</Badge>
          </div>
          <p className="mt-1 whitespace-pre-line text-sm text-zinc-600 dark:text-zinc-400">{n.message}</p>
          <p className="tabular mt-1 text-xs text-zinc-400">{fmt.dateTime(n.sent_at)}</p>
        </li>
      ))}
    </ul>
  );
}

function PasswordModal({ open, onClose, userId }) {
  const { t } = useI18n();
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setPassword(''); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/user/${userId}/set-credentials`, { password });
      toast.success(t('employees.passwordChanged'));
      onClose();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('employees.resetPasswordTitle')}
      description={t('employees.resetPasswordHint')}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="employee-password-form" loading={saving}>{t('common.save')}</Button>
        </>
      }
    >
      <form id="employee-password-form" onSubmit={submit}>
        <Field label={t('team.newPassword')} hint={t('team.passwordHint')}>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required autoComplete="new-password" />
        </Field>
      </form>
    </Modal>
  );
}

/** Версия приложения сотрудника и пометка, если она старее актуальной в App Store */
function AppVersionLine({ raw }) {
  const { t } = useI18n();
  const [latest, setLatest] = useState(null);
  useEffect(() => {
    api.get('/public/config').then((res) => setLatest(res.data?.app?.latest_version || null)).catch(() => {});
  }, []);
  // Старое приложение EnjoyFB всегда присылало «iOS-EnjoyFb/1.1.1» — настоящую версию оно не сообщает
  const legacy = /^iOS-EnjoyFb\//.test(raw);
  const version = legacy ? null : (String(raw).match(/(\d+(?:\.\d+){0,2})/) || [])[1];
  const older = (a, b) => {
    const pa = a.split('.').map(Number), pb = b.split('.').map(Number);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) < (pb[i] || 0);
    }
    return false;
  };
  const outdated = legacy || (version && latest && older(version, latest));
  return (
    <p className="flex flex-wrap items-center gap-1.5 text-xs text-zinc-500">
      {t('employees.appVersion')}: {legacy ? t('employees.legacyApp') : version || raw}
      {outdated && latest && <Badge tone="warning">{t('employees.updateAvailable', { version: latest })}</Badge>}
      {!outdated && version && latest && <Badge tone="success">{t('employees.upToDate')}</Badge>}
    </p>
  );
}
